/** The outcome recorded when a census was read successfully. */
const PARSED = "parsed";

/**
 * Why a read produced no usable census, in words an analyst can act on.
 *
 * Keyed by the outcomes `src/libs/backend/census` records. An outcome with no
 * wording here still reports as a failure rather than passing silently.
 */
const OUTCOME_REASONS: Readonly<Record<string, string>> = {
  encrypted: "the file is password protected",
  "legacy-workbook": "the file is an older Excel format we cannot open",
  unreadable: "the file could not be opened",
  "no-header-row": "no column headings could be found",
  "no-identity-columns": "it has no 'Date of Birth', 'ZIP' or 'Gender' column",
  "no-members": "no people could be read from it",
};

export const readSucceeded = (outcome: string): boolean => outcome === PARSED;

/**
 * How a finished read reads in a notification.
 *
 * It speaks only about the read. The checks on the page are refetched
 * separately and may not have arrived yet, or may fail to, so promising they
 * are current would sometimes be untrue. A failed read says why, because
 * nothing else will.
 */
export const readOutcomeMessage = (outcome: string): string => {
  if (readSucceeded(outcome)) {
    return "Census read successfully.";
  }

  const reason = OUTCOME_REASONS[outcome];

  return reason
    ? `The census could not be read: ${reason}.`
    : "The census could not be read.";
};

/**
 * How a read that produced no members reads on the census page. The file
 * arrived and was opened, so it speaks of processing rather than reading.
 */
export const processingFailedMessage = (outcome: string): string => {
  const reason = OUTCOME_REASONS[outcome];

  return reason
    ? `The census was received but could not be processed: ${reason}.`
    : "The census was received but could not be processed.";
};

/** Why a file gave no rows, as a clause: "the file is password protected". */
export const unreadableReason = (outcome: string): string =>
  OUTCOME_REASONS[outcome] ?? "the file could not be opened";
